import { Injectable, inject } from '@angular/core';
import { WebSocketService } from './websocket.service';
import { Observable, Subject, interval, Subscription } from 'rxjs';
import {ChunkUpdateNotification, PixelChunkRequest, PixelChunkResponse} from '@/app/core/models/p-place/pixel';

export type ReloadMode = 'periodic' | 'hybrid' | 'realtime' | 'off';

@Injectable({
	providedIn: 'root'
})
export class ChunkService {

	private webSocketService = inject(WebSocketService);

	private chunkResponses$ = new Subject<PixelChunkResponse>();
	private chunksResponses$ = new Subject<PixelChunkResponse[]>();
	private chunksUpdated$ = new Subject<ChunkUpdateNotification>();

	private currentVisibleChunks = new Set<string>();

	private chunksNeedingReload = new Set<string>();

	private reloadMode: ReloadMode = 'hybrid';
	private reloadIntervalMs = 10000;
	private reloadSubscription?: Subscription;
	private isReloadEnabled = false;

	constructor() {
		setTimeout(() => {
			this.subscribeToChunkResponses();
			this.subscribeToChunkUpdates();
		}, 0);
	}

	/**
	 * S'abonne aux réponses de chunks du WebSocket
	 */
	private subscribeToChunkResponses(): void {
		this.webSocketService.watch<PixelChunkResponse>('/topic/chunk-response')
			.subscribe({
				next: (response) => {
					this.chunkResponses$.next(response);
				},
				error: (error) => {
					console.error('❌ Erreur réception chunk unique:', error);
				}
			});

		this.webSocketService.watch<PixelChunkResponse[]>('/topic/chunks-response')
			.subscribe({
				next: (responses) => {
					this.chunksResponses$.next(responses);
				},
				error: (error) => {
					console.error('❌ Erreur réception chunks multiples:', error);
				}
			});
	}

	/**
	 * S'abonne aux notifications de mise à jour de chunks (depuis Redis invalidation)
	 */
	private subscribeToChunkUpdates(): void {
		this.webSocketService.watch<ChunkUpdateNotification>('/topic/chunks-updated')
			.subscribe({
				next: (notification) => {
					let visibleChunksAffected = 0;

					notification.affectedChunks.forEach(coord => {
						const key = this.getChunkKey(coord.chunkX, coord.chunkY);

						if (this.currentVisibleChunks.has(key)) {
							this.chunksNeedingReload.add(key);
							visibleChunksAffected++;
						}
					});

					if (visibleChunksAffected > 0) {
						if (this.reloadMode === 'realtime') {
							this.reloadPendingChunks();
						}
					}

					this.chunksUpdated$.next(notification);
				},
				error: (error) => {
					console.error('❌ Erreur lors de la réception des notifications:', error);
				}
			});
	}

	/**
	 * Recharge les chunks marqués comme nécessitant un rechargement
	 */
	private reloadPendingChunks(): void {
		if (this.chunksNeedingReload.size === 0) {
			return;
		}

		const requests: PixelChunkRequest[] = [];

		this.chunksNeedingReload.forEach(key => {
			const [chunkX, chunkY] = key.split(',').map(Number);
			requests.push({
				chunkX,
				chunkY,
				chunkSize: 100
			});
		});

		this.chunksNeedingReload.clear();

		if (requests.length > 0) {
			this.webSocketService.send('/app/request-chunks', requests);
		}
	}

	/**
	 * Recharge tous les chunks visibles (mode periodic)
	 */
	private reloadVisibleChunks(): void {
		if (this.currentVisibleChunks.size === 0) {
			return;
		}

		const requests: PixelChunkRequest[] = [];

		this.currentVisibleChunks.forEach(key => {
			const [chunkX, chunkY] = key.split(',').map(Number);
			requests.push({
				chunkX,
				chunkY,
				chunkSize: 100
			});
		});

		if (requests.length > 0) {
			this.webSocketService.send('/app/request-chunks', requests);
		}
	}

	/**
	 * Active le rechargement automatique
	 */
	startAutoReload(intervalMs: number = 10000, mode: ReloadMode = 'hybrid'): void {
		if (this.isReloadEnabled) {
			return;
		}

		this.reloadMode = mode;
		this.reloadIntervalMs = intervalMs;
		this.isReloadEnabled = true;

		if (mode === 'off') {
			return;
		}

		this.reloadSubscription = interval(intervalMs).subscribe(() => {
			if (mode === 'periodic') {
				this.reloadVisibleChunks();
			} else if (mode === 'hybrid') {
				this.reloadPendingChunks();
			}
		});
	}

	/**
	 * Désactive le rechargement automatique
	 */
	stopAutoReload(): void {
		if (!this.isReloadEnabled) {
			return;
		}

		this.isReloadEnabled = false;
		this.reloadSubscription?.unsubscribe();
		this.reloadSubscription = undefined;
	}

	/**
	 * Met à jour la liste des chunks visibles
	 */
	updateVisibleChunks(chunks: PixelChunkRequest[]): void {
		this.currentVisibleChunks.clear();

		chunks.forEach(chunk => {
			const key = this.getChunkKey(chunk.chunkX, chunk.chunkY);
			this.currentVisibleChunks.add(key);
		});
	}

	/**
	 * Demande plusieurs chunks
	 */
	requestMultipleChunks(requests: PixelChunkRequest[]): void {
		if (requests.length === 0) {
			return;
		}

		this.updateVisibleChunks(requests);

		this.webSocketService.send('/app/request-chunks', requests);
	}

	/**
	 * Observable pour recevoir les réponses de chunk unique
	 */
	onChunkReceived(): Observable<PixelChunkResponse> {
		return this.chunkResponses$.asObservable();
	}

	/**
	 * Observable pour recevoir les réponses de chunks multiples
	 */
	onChunksReceived(): Observable<PixelChunkResponse[]> {
		return this.chunksResponses$.asObservable();
	}

	/**
	 * Observable pour les notifications de mise à jour
	 */
	onChunksUpdated(): Observable<ChunkUpdateNotification> {
		return this.chunksUpdated$.asObservable();
	}

	/**
	 * Calcule quels chunks sont visibles dans le viewport
	 */
	calculateVisibleChunks(
		cameraX: number,
		cameraY: number,
		viewportWidth: number,
		viewportHeight: number,
		zoom: number,
		chunkSize: number
	): PixelChunkRequest[] {
		const visibleXStart = Math.floor(cameraX);
		const visibleYStart = Math.floor(cameraY);
		const visibleXEnd = Math.ceil(cameraX + viewportWidth / zoom);
		const visibleYEnd = Math.ceil(cameraY + viewportHeight / zoom);

		const chunkXStart = Math.floor(visibleXStart / chunkSize);
		const chunkXEnd = Math.floor(visibleXEnd / chunkSize);
		const chunkYStart = Math.floor(visibleYStart / chunkSize);
		const chunkYEnd = Math.floor(visibleYEnd / chunkSize);

		const requests: PixelChunkRequest[] = [];

		for (let chunkX = chunkXStart; chunkX <= chunkXEnd; chunkX++) {
			for (let chunkY = chunkYStart; chunkY <= chunkYEnd; chunkY++) {
				requests.push({ chunkX, chunkY, chunkSize });
			}
		}

		return requests;
	}

	/**
	 * Génère une clé unique pour un chunk
	 */
	private getChunkKey(chunkX: number, chunkY: number): string {
		return `${chunkX},${chunkY}`;
	}
}
